import React, { useMemo } from "react";
import { Box, Typography, Tooltip } from "@mui/material";

/** @type {Record<string, string>} */
const NAME_TO_HEX = {
	black: "#212121",
	charcoal: "#424242",
	grey: "#757575",
	gray: "#757575",
	white: "#eceff1",
	offwhite: "#f5f5f0",
	cream: "#faf8f3",
	beige: "#d7ccc8",
	navy: "#1a237e",
	blue: "#1565c0",
	royal: "#283593",
	sky: "#039be5",
	teal: "#00695c",
	green: "#2e7d32",
	olive: "#558b2f",
	red: "#c62828",
	maroon: "#6d1b1b",
	pink: "#ad1457",
	rose: "#d81b60",
	burgundy: "#880e4f",
	orange: "#ef6c00",
	rust: "#bf360c",
	yellow: "#f9a825",
	gold: "#fbc02d",
	brown: "#5d4037",
	tan: "#8d6e63",
	purple: "#6a1b9a",
	violet: "#4527a0",
	lavender: "#9575cd",
	silver: "#9e9e9e",
	mustard: "#c0a000",
	khaki: "#827717",
	coral: "#ff7043",
	mint: "#26a69a",
	peach: "#ffab91",
	turquoise: "#00838f",
	magenta: "#aa00ff",
	cyan: "#0097a7",
	ivory: "#fffff0",
	wine: "#722f37",
	multi: "#78909c",
	unknown: "#90a4ae",
};

function normalizeKey(s) {
	return String(s || "Unknown")
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]/g, "");
}

export function swatchColorFromName(raw) {
	const k = normalizeKey(raw);
	if (NAME_TO_HEX[k]) return NAME_TO_HEX[k];
	for (const [key, hex] of Object.entries(NAME_TO_HEX)) {
		if (k.includes(key) || key.includes(k)) return hex;
	}
	let h = 0;
	for (let i = 0; i < k.length; i++) h = ((h << 5) - h + k.charCodeAt(i)) | 0;
	h = Math.abs(h) % 360;
	return `hsl(${h}, 42%, 46%)`;
}

function segmentAmount(r) {
	const n = r.soldQty ?? r.gpValue ?? 0;
	return typeof n === "number" ? n : parseFloat(n) || 0;
}

function mergeForRamp(rows, maxSegments) {
	if (!rows || rows.length === 0) return [];
	if (rows.length <= maxSegments) return rows.map((r) => ({ ...r }));
	const head = rows.slice(0, maxSegments - 1).map((r) => ({ ...r }));
	const tail = rows.slice(maxSegments - 1);
	const otherAmt = tail.reduce((s, r) => s + segmentAmount(r), 0);
	const total = rows.reduce((s, r) => s + segmentAmount(r), 0);
	const otherPct = total > 0 ? Math.round((otherAmt / total) * 1000) / 10 : 0;
	return [
		...head,
		{
			color: "Other",
			soldQty: otherAmt,
			percentage: otherPct,
			count: tail.reduce((s, r) => s + (r.count || 0), 0),
		},
	];
}

function formatLabelPct(p) {
	return p % 1 === 0 ? `${Math.round(p)}` : p.toFixed(1);
}

/**
 * Colour ramp: each segment’s width and label = its GP contribution as % of total GP in the loaded slice (not “share” wording elsewhere).
 */
export default function ColorContributionRamp({
	rows,
	title = "COLOR CONTRIBUTION",
	variant = "light",
	maxSegments = 10,
	barHeight = 32,
}) {
	const segments = useMemo(() => mergeForRamp(rows || [], maxSegments), [rows, maxSegments]);

	if (!segments.length) return null;

	const labelColor = variant === "dark" ? "rgba(255,255,255,0.88)" : "#424242";
	const titleColor = variant === "dark" ? "rgba(255,255,255,0.72)" : "#616161";
	const segmentDivider =
		variant === "dark" ? "1px solid rgba(0,0,0,0.22)" : "1px solid rgba(0,0,0,0.06)";

	return (
		<Box sx={{ width: "100%" }}>
			<Typography
				variant="caption"
				component="div"
				sx={{
					color: titleColor,
					fontWeight: 600,
					letterSpacing: "0.07em",
					fontSize: variant === "dark" ? "0.65rem" : "0.6rem",
					mb: 0.75,
					textTransform: "uppercase",
				}}>
				{title}
			</Typography>
			<Box sx={{ width: "100%" }}>
				<Box
					sx={{
						display: "flex",
						flexDirection: "row",
						width: "100%",
						borderRadius: "4px",
						overflow: "hidden",
						boxShadow:
							variant === "light"
								? "inset 0 0 0 1px rgba(0,0,0,0.07)"
								: "inset 0 0 0 1px rgba(255,255,255,0.1)",
					}}>
					{segments.map((row, i) => (
						<Tooltip
							key={`${row.color}-${i}`}
							title={
								row.soldQty != null
									? `${row.color}: ${row.percentage.toFixed(1)}% of sold qty · ${row.soldQty} pcs`
									: `${row.color}: ${row.percentage.toFixed(1)}% of total in this view`
							}
							arrow>
							<Box
								sx={{
									flex: `0 1 ${row.percentage}%`,
									minWidth: 4,
									height: barHeight,
									backgroundColor: swatchColorFromName(row.color),
									borderRight: i < segments.length - 1 ? segmentDivider : "none",
									cursor: "default",
								}}
							/>
						</Tooltip>
					))}
				</Box>
				<Box sx={{ display: "flex", flexDirection: "row", width: "100%", mt: 0.5 }}>
					{segments.map((row, i) => (
						<Box
							key={`${row.color}-lbl-${i}`}
							sx={{
								flex: `0 1 ${row.percentage}%`,
								minWidth: 4,
								textAlign: "center",
								px: 0.125,
							}}>
							<Typography
								variant="caption"
								component="div"
								sx={{
									color: labelColor,
									fontSize: variant === "dark" ? "0.58rem" : "0.55rem",
									fontWeight: 600,
									lineHeight: 1.15,
								}}>
								{formatLabelPct(row.percentage)}%
							</Typography>
						</Box>
					))}
				</Box>
			</Box>
		</Box>
	);
}
